/**
 * Wording for the assistant's profile, from Autopilot's inbox, jobs and memories (Ask Addy v2).
 * From the lendy-ui branch's design; kept free of React so it can be tested on its own.
 */

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const DECIDED_STATUSES = ["approved", "declined", "expired", "dismissed"];
const OUTCOME_LABELS = { approved: "Approved", declined: "Declined", expired: "Expired" };
const MARKDOWN_MARKS = /[#*_`>[\]]/g;

const ITEM_ICONS = { report: "check", approval: "shield", memory: "book", error: "alert" };
const JOB_ICONS = { heartbeat: "pulse", schedule: "calendar", event: "home" };
const LAST_RUN_LABELS = {
  reported: "reported",
  quiet: "nothing new",
  needs_approval: "waiting for you",
  error: "failed",
  running: "running",
};
const MEMORY_SOURCES = { lo: "you added", curator: "learned" };
const TOOL_LABELS = {
  gmail_send_email: "Send an email from your Gmail",
  update_loan_notes: "Update the loan notes",
  update_loan_details: "Update loan details",
  update_loan_stage: "Change the loan stage",
  update_task_status: "Change a task's status",
  update_task_instructions: "Change a task's instructions",
  rerun_task_with_instructions: "Rerun a task",
  request_documents: "Request documents from the borrower",
  fill_workbook: "Fill the worksheet",
  apps_run: "An action in one of your apps",
  autopilot_create_job: "Set up a scheduled task",
  autopilot_update_job: "Change a scheduled task",
};

export const JOB_GROUPS = [
  { kind: "heartbeat", label: "Repeating" },
  { kind: "schedule", label: "Scheduled" },
  { kind: "event", label: "When a loan changes" },
];


const startOfDay = (ms) => {
  const day = new Date(ms);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
};

/** "just now", "12 min ago", "5 h ago", then the date. */
export const formatAgo = (ms, now = Date.now()) => {
  const elapsedMs = now - ms;
  if (elapsedMs < MINUTE_MS) {
    return "just now";
  }
  if (elapsedMs < HOUR_MS) {
    return `${Math.round(elapsedMs / MINUTE_MS)} min ago`;
  }
  if (elapsedMs < DAY_MS) {
    return `${Math.round(elapsedMs / HOUR_MS)} h ago`;
  }
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/** The time an item landed, for the activity list: "8:00 AM". */
export const formatTimeOfDay = (ms) =>
  new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const LOCAL_TIME_ZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return undefined;
  }
})();

/**
 * The next run in the job's own time zone, named ("Thu 8:00 AM IST"); when that isn't this
 * computer's zone, also when it is here, so a job set for another zone is never a surprise.
 */
export const formatNextRun = (ms, timeZone, localTimeZone = LOCAL_TIME_ZONE) => {
  if (!ms) {
    return "";
  }
  const options = { weekday: "short", hour: "numeric", minute: "2-digit" };
  const format = (zone, isNamed) => {
    try {
      return new Date(ms).toLocaleString(undefined, { ...options, timeZone: zone, ...(isNamed ? { timeZoneName: "short" } : {}) });
    } catch (error) {
      return new Date(ms).toLocaleString(undefined, options);
    }
  };
  if (!timeZone || timeZone === localTimeZone) {
    return format(localTimeZone, true);
  }
  return `${format(timeZone, true)} (${format(localTimeZone, false)} your time)`;
};

/** "Today", "Yesterday", or the date. */
export const getDayLabel = (ms, now = Date.now()) => {
  const days = Math.round((startOfDay(now) - startOfDay(ms)) / DAY_MS);
  if (days === 0) {
    return "Today";
  }
  if (days === 1) {
    return "Yesterday";
  }
  return new Date(ms).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
};

/** Items under day labels, in the order they came (the inbox is newest first). */
export const groupByDay = (items, now = Date.now()) =>
  items.reduce((groups, item) => {
    const label = getDayLabel(item.createdAt, now);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.label === label) {
      return [...groups.slice(0, -1), { ...lastGroup, items: [...lastGroup.items, item] }];
    }
    return [...groups, { label, items: [item] }];
  }, []);

/** An approval, or a "remember this?", that the loan officer hasn't answered yet. */
export const isWaitingForYou = ({ kind, status }) =>
  (kind === "approval" || kind === "memory") && !DECIDED_STATUSES.includes(status);

export const getItemIcon = ({ kind }) => ITEM_ICONS[kind] || "bell";

/** One line under an inbox item: whether it waits on you, how it ended, or how its report starts. */
export const getItemSummary = (item) => {
  if (isWaitingForYou(item)) {
    return "Waiting for you";
  }
  if (OUTCOME_LABELS[item.status]) {
    return OUTCOME_LABELS[item.status];
  }
  const firstLine = (item.body || "")
    .split("\n")
    .map((line) => line.replace(MARKDOWN_MARKS, "").trim())
    .find(Boolean);
  return firstLine || "";
};

/** What an approval would do, in words: "Send an email", or the tool's own name. */
export const getApprovalLabel = (approval) => {
  if (!approval?.toolName) {
    return "";
  }
  const { toolName } = approval;
  return TOOL_LABELS[toolName] || toolName.replace(/_/g, " ");
};

/** The line under an approval: who an email goes to and its subject, or what the tool does. */
export const getApprovalDetail = (approval) => {
  const { to, subject } = approval?.args || {};
  if (approval?.toolName === "gmail_send_email" && (to || subject)) {
    return [to && `To ${to}`, subject].filter(Boolean).join(" · ");
  }
  return getApprovalLabel(approval);
};

export const getJobIcon = ({ kind }) => JOB_ICONS[kind] || "clock";

/** "All my work", or which loan the job watches. */
export const getJobScope = ({ loanId }, currentLoanId) => {
  if (!loanId) {
    return "All my work";
  }
  return loanId === currentLoanId ? "This loan" : "Another loan";
};

/** One line under a job: when it runs, then how its last run went. */
export const getJobSummary = (job, now = Date.now()) => {
  const { scheduleText, enabled, lastRunAt, lastStatus } = job;
  if (!enabled) {
    return [scheduleText, "Paused"].filter(Boolean).join(" · ");
  }
  if (!lastRunAt) {
    return scheduleText || "";
  }
  const outcome = LAST_RUN_LABELS[lastStatus];
  const lastRun = `ran ${formatAgo(lastRunAt, now)}${outcome ? `, ${outcome}` : ""}`;
  return [scheduleText, lastRun].filter(Boolean).join(" · ");
};

/** The job to create, in the shape the v2 Autopilot panel sends. */
export const buildJob = ({ name, prompt, timing, time, everyMinutes, loanId, isThisLoan, timeZone, activeHours }) => {
  const isLoanJob = (timing === "event" || isThisLoan) && !!loanId;
  const job = {
    name,
    prompt,
    scope: isLoanJob ? "loan" : "general",
    loanId: isLoanJob ? loanId : undefined,
    timezone: timeZone,
    ...(activeHours ? { activeHours } : {}),
  };
  if (timing === "heartbeat") {
    return { ...job, kind: "heartbeat", everyMinutes: Number(everyMinutes) };
  }
  if (timing === "event") {
    return { ...job, kind: "event", events: ["*"] };
  }
  const [hours, minutes] = time.split(":").map(Number);
  const days = timing === "weekdays" ? "1-5" : "*";
  return { ...job, kind: "schedule", schedule: `${minutes} ${hours} * * ${days}` };
};

/** "You · lesson · saved in chat" */
export const getMemoryMeta = ({ scope, category, createdBy }) =>
  [
    scope === "org" ? "Team" : "You",
    (category || "").replace(/_/g, " "),
    MEMORY_SOURCES[createdBy] || "saved in chat",
  ]
    .filter(Boolean)
    .join(" · ");

/** The line under Lendy's name: what needs you first, then whether any job is on; nothing when idle. */
export const getAutopilotState = ({ inbox, jobs }) => {
  const waitingCount = (inbox?.items || []).filter(isWaitingForYou).length;
  if (waitingCount > 0) {
    return { text: `${waitingCount} waiting for you`, tone: "wait" };
  }
  if ((jobs || []).some(({ enabled }) => enabled)) {
    return { text: "Scheduled tasks on", tone: "ready" };
  }
  return { text: "", tone: "ready" };
};
